import {GetFormattedOrder} from '@/models/OnlineOrderModel'
import {getOnlineOrder, getOnlineOrderCsvFile} from '@/services/Order/OnlineOrder/onlineOrderList'
import {OnlineOrderListColConfig} from '@/services/MockData/Order/OrderList/OnlineOrderColConfig'
import {useQueries} from '@tanstack/react-query'
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils'
import {useDataTable} from '@/hooks/useDataTable-hook'
import {useMemo, useEffect} from 'react'
import {useSelector} from 'react-redux'
import {selectCountryCode} from '@/store/slices/countryCodeSlice'
import {filterColumnsByFeatures, setDefaultFilterValues} from '@/utils/columnConfigUtils'
import {showOrderTypeFeature} from '@/utils/commons'

export const useOnlineOrderList = () => {
  const {setPageIndex, setSortingObj, setSearchArry, resetSearchArr, pageIndex, sortingObj, searchArr, handleResetAll}= useDataTable()
  const countryCode = useSelector(selectCountryCode) || localStorage.getItem("countryCode"); // Get countryCode from Redux store

  // Filter columns based on feature flags and country code
  const filteredColumns = useMemo(() => {
    const featureFlags = {
      showOrderTypeFeature
    };
    return filterColumnsByFeatures(OnlineOrderListColConfig, countryCode, featureFlags);
  }, [countryCode])

  // Set default filters based on column configuration
  useEffect(() => {
    setDefaultFilterValues(filteredColumns, searchArr, setSearchArry);
  }, [searchArr, setSearchArry, filteredColumns])

  const queryParams = useMemo(() => {
    const params = new URLSearchParams()
    if (sortingObj?.column) {
      params.append('sortBy', sortingObj?.column)
      params.append('order', sortingObj?.direction)
    }
    // Add pagination parameters
    params.append('pageSize', 10)
    params.append('page', pageIndex || 0)

    return params.toString()
  }, [sortingObj?.column, sortingObj?.direction, pageIndex])

  // Check if date range is complete
  const isValidDateRange = useMemo(() => {
    if (!searchArr?.length) return true;
    const dateRangeSearch = searchArr.find(s => s.type === 'dateRange');
    if (!dateRangeSearch) return true;
    const [startDate, endDate] = dateRangeSearch.value.split(',');
    return Boolean(startDate && endDate && !dateRangeSearch.value.endsWith(','));
  }, [searchArr])

  const [{data, isLoading, isFetching}] = useQueries({
    queries: [
      {
        queryKey: ['ONLINE_ORDER', queryParams, searchArr],
        queryFn: () => getOnlineOrder(searchArr, queryParams),
        enabled: isValidDateRange 
      },
    ],
  })

  const onDownloadBtnClick = async selectedIds => {
    const selectedIdsArr = selectedIds.join(',');
    const tmpParams = new URLSearchParams(queryParams)
    tmpParams.delete('pageSize') // Remove pageSize for download
    tmpParams.delete('page') // Remove page for download
    tmpParams.toString()
    const defaultSortingObj =
      Object.keys(tmpParams)?.length > 0
        ? `${tmpParams}`
        : `orderType=ONLINE`

    const body = {
      ids: selectedIdsArr,
      filters: searchArr,
    }

    const data = await getOnlineOrderCsvFile(body, defaultSortingObj)
    const fileName = `OnlineOrder_${getDateWithTimeString()}`
    downloadCSVFiles(data, fileName)
  }

  const resultsData = useMemo(() => data || {}, [data])
  const onlineOrderData = useMemo(() => GetFormattedOrder(resultsData), [resultsData])

  return {
    sortingObj,
    searchArr,
    onlineOrderData,
    OnlineOrderListColConfigData: filteredColumns,
    pageIndex,
    isLoading: isLoading || isFetching,
    setPageIndex,
    setSortingObj,
    setSearchArry,
    onDownloadBtnClick,
    resetSearchArr,
    handleResetAll
  }
}
